import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import BottomNav from "./BottomNav";
import { AppBootError } from "./AppBootError";
import SubscriptionBanner from "./SubscriptionBanner";
import { SubscriptionBlockScreen } from "./subscription/SubscriptionBlockScreen";
import { OverdueDailyDialog } from "./subscription/OverdueDailyDialog";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { computeSubscriptionGate } from "@/lib/subscriptionGate";
import { useIdleRoutePrefetch } from "@/hooks/useIdleRoutePrefetch";

const AppLayout = () => {
  const { pathname } = useLocation();
  const { subscriptionStatus, cancelAtPeriodEnd, pastDueSince, loading, error, retry } =
    useSubscriptionStatus();

  // Gate de assinatura atrasada (item D). Enquanto `pastDueSince` for null
  // (PR 1 — backend ainda não popula), `bloqueado` é sempre false → dark.
  const gate = computeSubscriptionGate({
    status: subscriptionStatus,
    pastDueSince,
  });
  useIdleRoutePrefetch(
    pathname,
    !loading && subscriptionStatus !== "canceled" && !gate.bloqueado,
  );

  const [overdueOpen, setOverdueOpen] = useState(false);
  const [blockedByApi, setBlockedByApi] = useState(false);

  // O bootstrap é um retrato. Se qualquer chamada paga receber o contrato
  // estável 402 do backend, o shell entra imediatamente no mesmo modo de
  // recuperação sem exibir detalhes internos da resposta.
  useEffect(() => {
    const previousFetch = window.fetch;
    const apiBase = import.meta.env.VITE_API_URL || "https://api.ondiet.app";
    const apiOrigin = new URL(apiBase, window.location.origin).origin;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await previousFetch(...args);
      const target = args[0] instanceof Request ? args[0].url : String(args[0]);
      const targetUrl = new URL(target, window.location.origin);
      if (
        response.status === 402
        && targetUrl.origin === apiOrigin
        && targetUrl.pathname.startsWith("/api/")
      ) {
        setBlockedByApi(true);
      }
      return response;
    };
    return () => {
      window.fetch = previousFetch;
    };
  }, []);

  // Pop-up de aviso enquanto atrasado mas não bloqueado (dias 0-6), em toda
  // inicialização do shell. Guardado por `!loading` pra não abrir com dado
  // ainda não confirmado.
  useEffect(() => {
    if (!loading && gate.mostrarAvisoDiario) {
      setOverdueOpen(true);
    }
  }, [loading, gate.mostrarAvisoDiario]);

  if (!blockedByApi && error) {
    return <AppBootError onRetry={retry} />;
  }

  if (blockedByApi || (!loading && gate.bloqueado)) {
    return <SubscriptionBlockScreen canceled={subscriptionStatus === "canceled"} />;
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      <SubscriptionBanner status={subscriptionStatus} cancelAtPeriodEnd={cancelAtPeriodEnd} />
      <OverdueDailyDialog
        open={overdueOpen}
        onOpenChange={setOverdueOpen}
        diasAtrasado={gate.diasAtrasado}
      />
      <main>
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
};

export default AppLayout;
